/**
 * Unidades do cliente.
 * Cada unidade pertence a exatamente um cliente.
 * Valida client_id no backend.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCapabilities } from "@/hooks/useCapabilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Factory, X, Save } from "lucide-react";

export default function UnitsTab({ clientId }) {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState("");
  // Mesma decisão das rotas /units (backend/capabilities.py), no cliente selecionado.
  const caps = useCapabilities(["unidades.create", "unidades.edit", "unidades.delete"], { clientId });

  const loadUnits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/units", { params: { client_id: clientId } });
      const list = Array.isArray(data) ? data : data?.items || [];
      setUnits(list.filter((u) => u.client_id === clientId));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao carregar unidades");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadUnits(); }, [loadUnits]);

  const handleCreate = () => {
    setEditing("new");
    setFormName("");
  };

  const handleEdit = (unit) => {
    setEditing(unit.id);
    setFormName(unit.name || "");
  };

  const handleCancel = () => {
    setEditing(null);
    setFormName("");
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error("Nome da unidade e obrigatorio");
      return;
    }
    try {
      if (editing === "new") {
        await api.post("/units", { client_id: clientId, name: formName });
        toast.success("Unidade criada");
      } else {
        await api.put(`/units/${editing}`, { client_id: clientId, name: formName });
        toast.success("Unidade atualizada");
      }
      handleCancel();
      loadUnits();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao salvar");
    }
  };

  const handleDelete = async (unitId) => {
    if (confirm("Excluir esta unidade?")) {
      try {
        await api.delete(`/units/${unitId}`);
        toast.success("Unidade excluida");
        loadUnits();
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao excluir");
      }
    }
  };

  if (loading) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Badge variant="outline">{units.length} unidade(s)</Badge>
        {caps.can("unidades.create") && (
          <Button size="sm" onClick={handleCreate} data-testid="units-new">
            <Plus className="h-4 w-4 mr-1" />Nova Unidade
          </Button>
        )}
      </div>

      {editing && (
        <Card className="p-3 border-primary">
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Nome da unidade"
              data-testid="units-form-name"
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            />
            <Button size="sm" onClick={handleSave} data-testid="units-form-save"><Save className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" onClick={handleCancel}><X className="h-4 w-4" /></Button>
          </div>
        </Card>
      )}

      {units.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          Nenhuma unidade cadastrada
        </p>
      ) : (
        <div className="divide-y divide-border border border-border rounded-md">
          {units.map((unit) => (
            <div key={unit.id} className="flex items-center justify-between p-3">
              <div className="flex items-center gap-2">
                <Factory className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{unit.name}</span>
              </div>
              <div className="flex gap-1">
                {caps.can("unidades.edit") && (
                  <Button size="icon" variant="ghost" onClick={() => handleEdit(unit)} data-testid={`unit-edit-${unit.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
                {caps.can("unidades.delete") && (
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(unit.id)} data-testid={`unit-delete-${unit.id}`}>
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
